import { useState } from "react";
import ReactMarkdown from "react-markdown";
import useDataset from "datasets/hooks/useDataset";
import useDatasetPermission from "datasets/hooks/useDatasetPermission";
import useStatistics from "datasets/hooks/useStatistics";
import { DatasetAccessLevel } from "datasets/types/enums/access-level";
import Dataset from "datasets/types/interfaces/dataset";
import { format } from "date-fns";
import { CalendarCheck, CalendarPlus, HardDrive, Pencil, Table2 } from "lucide-react";
import pluralize from "pluralize";
import prettyBytes from "pretty-bytes";
import remarkGfm from "remark-gfm";
import { ReadOnlyBadge } from "~/components/ReadOnlyBadge";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Skeleton } from "~/components/ui/skeleton";
import { Textarea } from "~/components/ui/textarea";
import useToast from "~/components/ui/toast";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { apiMutate } from "~/utils/apiClient";

const { VITE_CATALOG_API } = import.meta.env;

const formatDate = (timestamp: string) => format(new Date(timestamp), "MMM dd, yyyy");

interface DescriptionModalProps {
  dataset: Dataset;
  onUpdate: () => void;
  onClose: () => void;
}

const DescriptionModal = ({ dataset, onUpdate, onClose }: DescriptionModalProps) => {
  const { showSuccess, showError } = useToast();
  const [text, setText] = useState(dataset.description ?? "");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiMutate<Dataset>(`${VITE_CATALOG_API}/datasets/${dataset.id}`, "PATCH", {
        description: text,
      });
      onUpdate();
      onClose();
      showSuccess("Dataset description updated");
    } catch (e) {
      showError(e instanceof Error ? e.message : "Failed to update dataset description");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="flex max-h-[90vh] flex-col sm:max-w-4xl" showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>Edit Description</DialogTitle>
        </DialogHeader>
        <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto">
          <p className="text-xs text-muted-foreground">
            Supports Markdown: <strong>**bold**</strong>, <em>*italic*</em>, lists, and more.
          </p>
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={12}
            className="font-mono text-sm focus-visible:ring-0"
            disabled={saving}
            autoFocus
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const DatasetOverview = () => {
  const { data: dataset, isLoading, mutate } = useDataset();
  const { data: stats, isLoading: loadingStats } = useStatistics(dataset?.id);
  const hasEditPermission = useDatasetPermission("edit:catalog-dataset");
  const canEdit = dataset?.access_level === DatasetAccessLevel.OWNER && hasEditPermission;

  const [descriptionModalOpen, setDescriptionModalOpen] = useState(false);

  if (isLoading) {
    return (
      <div
        role="status"
        aria-label="Loading overview"
        className="mx-auto flex w-full max-w-3xl flex-col gap-4"
      >
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-6 w-72" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  if (!dataset) return null;

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 pb-12">
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-semibold">Overview</h1>
          {!canEdit && <ReadOnlyBadge />}
        </div>

        <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
          <Tooltip delayDuration={300}>
            <TooltipTrigger asChild>
              <span className="flex cursor-default items-center gap-1.5">
                <Table2 className="size-4" aria-hidden="true" />
                {`${dataset.table_count} ${pluralize("Table", dataset.table_count)}`}
              </span>
            </TooltipTrigger>
            <TooltipContent>Total tables</TooltipContent>
          </Tooltip>

          <Tooltip delayDuration={300}>
            <TooltipTrigger asChild>
              <span className="flex cursor-default items-center gap-1.5">
                <HardDrive className="size-4" aria-hidden="true" />
                {loadingStats ? (
                  <Skeleton className="h-4 w-16" />
                ) : stats?.table_total_bytes !== undefined ? (
                  prettyBytes(stats.table_total_bytes)
                ) : (
                  "? bytes"
                )}
              </span>
            </TooltipTrigger>
            <TooltipContent>Dataset size</TooltipContent>
          </Tooltip>

          {dataset.created_at && (
            <Tooltip delayDuration={300}>
              <TooltipTrigger asChild>
                <span className="flex cursor-default items-center gap-1.5">
                  <CalendarPlus className="size-4" aria-hidden="true" />
                  {formatDate(dataset.created_at)}
                </span>
              </TooltipTrigger>
              <TooltipContent>Date created</TooltipContent>
            </Tooltip>
          )}

          {dataset.updated_at && (
            <Tooltip delayDuration={300}>
              <TooltipTrigger asChild>
                <span className="flex cursor-default items-center gap-1.5">
                  <CalendarCheck className="size-4" aria-hidden="true" />
                  {formatDate(dataset.updated_at)}
                </span>
              </TooltipTrigger>
              <TooltipContent>Last updated</TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>

      <div className="group relative">
        <div className="prose prose-sm max-w-none prose-headings:font-semibold prose-headings:text-foreground prose-p:text-foreground prose-li:text-foreground prose-strong:text-foreground prose-em:text-foreground">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {dataset.description || "_No description provided._"}
          </ReactMarkdown>
        </div>
        {canEdit && (
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-0 top-0 size-7 opacity-0 transition-opacity group-hover:opacity-100"
            aria-label="Edit description"
            onClick={() => setDescriptionModalOpen(true)}
          >
            <Pencil className="size-3.5" />
          </Button>
        )}
      </div>

      {descriptionModalOpen && (
        <DescriptionModal
          dataset={dataset}
          onUpdate={() => mutate()}
          onClose={() => setDescriptionModalOpen(false)}
        />
      )}
    </div>
  );
};
